import React from 'react';
import PropTypes from 'prop-types';
import { Animate } from 'react-move';
import { easeLinear } from 'd3-ease';
import { connect } from 'react-redux';
import Media from 'components/Media';
import { formatCurrency } from 'lib/formatters';
import Bingo from 'modules/Bingo';
import Tooltip from 'components/Tooltip';
import Card75 from '../Card75';
import Card90 from '../Card90';
import PrizeRow from '../PrizeRow';
import './gamePrizes.css';
import './jackpotToGo.css';
import './progressiveJackpotPrize.css';

const S = Bingo.constants.STAGE;

export const _BingoInfoPanel = ({
  priceLabel,
  ticketPrice,
  jackpotAmount,
  oneLineJackpotAmount,
  twoLinesJackpotAmount,
  oneLineWinners,
  twoLineWinners,
  progressiveJackpotAmount,
  progressiveJackpotCalls,
  progressiveJackpotMaxCalls,
  players,
  type,
  patternPositions,
  stage,
  inPlay,
  inProgress,
  isRollover,
  logoImageUrl,
  inProgressStatus
}) => {
  const progressiveJackpot = (
    <Animate
      // Set some default data
      start={{
        scale: 1,
        progressiveJackpotAmount: progressiveJackpotAmount
      }}
      update={{
        progressiveJackpotAmount: [progressiveJackpotAmount],
        timing: { duration: 1000, ease: easeLinear }
      }}
    >
      {(data) => (
        <span className="progressiveJackpotAmount">
          {formatCurrency(data.progressiveJackpotAmount)}
        </span>
      )}
    </Animate>
  );
  const showProgressive =
    progressiveJackpotAmount !== null &&
    (((inPlay || inProgress) && progressiveJackpotCalls !== 0) || (!inPlay && !inProgress));

  const value =
    ticketPrice === 0 ? priceLabel : formatCurrency(ticketPrice, { precise: true, pennies: false });

  return (
    <div className="bingoInfoPanel">
      <Media query="(max-width: 1024px)">
        {(matches) =>
          !matches && showProgressive ? (
            <div className={`progressiveJackpotPrize prizeBlock g${type} loaded`}>
              <div className="prizeRow" tabIndex={-1}>
                <span>
                  {isRollover ? 'Rollover Jackpot' : 'Progressive Jackpot'}
                  <span className="jackBall progressiveJackpotBalls">
                    {inPlay || inProgress ? progressiveJackpotCalls : progressiveJackpotMaxCalls}
                  </span>
                </span>
                {progressiveJackpot}
              </div>
            </div>
          ) : null
        }
      </Media>
      <div className={`gamePrizes prizeBlock g${type} loaded${isRollover ? ' rollover' : ''}`}>
        <header>
          {type === Bingo.constants.GAME_TYPE['90'] && (
            <Card90
              stage={stage}
              inProgressStatus={inProgressStatus}
              inPlay={inPlay || inProgress}
            />
          )}
          {type === Bingo.constants.GAME_TYPE['75'] && <Card75 positions={patternPositions} />}
          <div className="price info-box">
            <img className="info-box__icon" src="/assets/images1/price.svg" alt="Price" />
            <span className="tag">
              <span className="info-title">Price</span>
              <Tooltip
                label={value}
                position="above"
                className="info-box__value ticketPrice"
                id="mainTicketPrice"
                showOnOverflow
              >
                {value}
              </Tooltip>
            </span>
          </div>
          <div className="players info-box">
            <img className="info-box__icon" src="/assets/images1/players.svg" alt="Players" />
            <span className="tag">
              <span className="info-title">Players</span>
              <Tooltip
                label={players}
                position="above"
                className="info-box__value players playersCount"
                showOnOverflow
              >
                {players}
              </Tooltip>
            </span>
          </div>
        </header>
        <Media query="(max-width: 1024px)">
          {(matches) => (
            <div className="prizeRow" tabIndex={-1}>
              {matches && showProgressive ? (
                <PrizeRow
                  className="withRoomLogo"
                  logoImageUrl={logoImageUrl}
                  title={isRollover ? 'Rollover' : 'Progressive'}
                  jackpotAmount={progressiveJackpotAmount}
                  balls={
                    inPlay || inProgress ? progressiveJackpotCalls : progressiveJackpotMaxCalls
                  }
                  showMore={!isRollover || !!(twoLinesJackpotAmount || oneLineJackpotAmount)}
                />
              ) : null}
              {!isRollover ? (
                <PrizeRow
                  title={type === Bingo.constants.GAME_TYPE['90'] ? 'Full House' : 'Jackpot'}
                  className={matches && !showProgressive ? 'withRoomLogo' : ''}
                  logoImageUrl={matches && !showProgressive ? logoImageUrl : ''}
                  jackpotAmount={jackpotAmount}
                  showMore={
                    (!showProgressive || !matches) &&
                    !!(twoLinesJackpotAmount || oneLineJackpotAmount)
                  }
                />
              ) : null}
              {twoLinesJackpotAmount !== null ? (
                <PrizeRow
                  title="2 Lines"
                  jackpotAmount={twoLinesJackpotAmount}
                  winner={twoLineWinners}
                />
              ) : null}
              {oneLineJackpotAmount !== null ? (
                <PrizeRow
                  title="1 Line"
                  jackpotAmount={oneLineJackpotAmount}
                  winner={oneLineWinners}
                />
              ) : null}
            </div>
          )}
        </Media>
      </div>
    </div>
  );
};

_BingoInfoPanel.propTypes = {
  type: PropTypes.oneOf(['30', '75', '90']).isRequired,
  priceLabel: PropTypes.string,
  jackpotAmount: PropTypes.number,
  oneLineJackpotAmount: PropTypes.number,
  twoLinesJackpotAmount: PropTypes.number,
  oneLineWinners: PropTypes.object,
  twoLineWinners: PropTypes.object,
  progressiveJackpotAmount: PropTypes.number,
  progressiveJackpotCalls: PropTypes.number,
  progressiveJackpotMaxCalls: PropTypes.number,
  ticketPrice: PropTypes.number,
  players: PropTypes.number,
  stage: PropTypes.string,
  inProgressStatus: PropTypes.string,
  logoImageUrl: PropTypes.string,
  inPlay: PropTypes.bool,
  inProgress: PropTypes.bool,
  isRollover: PropTypes.bool,
  patternPositions: PropTypes.array
};

_BingoInfoPanel.defaultProps = {
  priceLabel: null,
  jackpotAmount: 0,
  oneLineJackpotAmount: null,
  twoLinesJackpotAmount: null,
  oneLineWinners: null,
  twoLineWinners: null,
  progressiveJackpotAmount: null,
  progressiveJackpotCalls: null,
  progressiveJackpotMaxCalls: null,
  ticketPrice: 0,
  players: 0,
  inPlay: false,
  inProgress: false,
  isRollover: false,
  stage: S.WAITING_FIRST_LINE,
  inProgressStatus: '',
  logoImageUrl: '',
  patternPositions: []
};

const mapStateToProps = (state, ownProps) => {
  const { refName } = ownProps;
  const roomProps = Bingo.selectors.getDisplayProps(state) || {};
  const startDate = Bingo.selectors.getNextStartDate(state, refName);
  const inPlay = Bingo.selectors.getInPlay(state);
  const inProgress = Bingo.selectors.getInProgress(state);
  const { type, ticketPrice, logoImageUrl } = roomProps;
  return {
    patternPositions: Bingo.selectors.getPattern(
      state,
      refName,
      inPlay || inProgress ? 'current' : 'next'
    ),
    startDate: startDate && new Date(startDate).getTime(),
    players:
      inPlay || inProgress
        ? Bingo.selectors.getPlayers(state, refName, 'current')
        : Bingo.selectors.getPlayers(state, refName, 'next'),
    type: type || ownProps.type,
    ticketPrice: ticketPrice || 0,
    progressiveJackpotAmount:
      Bingo.selectors.getProgressiveJackpotAmount(state, refName, 'next') ||
      Bingo.selectors.getProgressiveJackpotAmount(state),
    progressiveJackpotCalls: Bingo.selectors.getProgressiveJackpotCalls(state, refName, 'current'),
    progressiveJackpotMaxCalls:
      Bingo.selectors.getProgressiveJackpotMaxCalls(state, refName, 'current') ||
      Bingo.selectors.getProgressiveJackpotMaxCalls(state),
    jackpotAmount:
      inPlay || inProgress
        ? Bingo.selectors.getGameJackpotAmount(state, refName, 'current')
        : Bingo.selectors.getGameJackpotAmount(state),
    oneLineJackpotAmount:
      inPlay || inProgress
        ? Bingo.selectors.getOneLineJackpotAmount(state, refName, 'current')
        : Bingo.selectors.getOneLineJackpotAmount(state),
    twoLinesJackpotAmount:
      inPlay || inProgress
        ? Bingo.selectors.getTwoLinesJackpotAmount(state, refName, 'current')
        : Bingo.selectors.getTwoLinesJackpotAmount(state),
    oneLineWinners:
      inPlay || inProgress
        ? Bingo.selectors.getOneLineWinners(state, refName, 'current')
        : Bingo.selectors.getOneLineWinners(state),
    twoLineWinners:
      inPlay || inProgress
        ? Bingo.selectors.getTwoLinesWinners(state, refName, 'current')
        : Bingo.selectors.getTwoLinesWinners(state),
    inPlay: inPlay,
    inProgress: inProgress,
    isRollover: Bingo.selectors.getRollover(
      state,
      refName,
      inPlay || inProgress ? 'current' : 'next'
    ),
    logoImageUrl: logoImageUrl,
    stage: Bingo.selectors.getWaitingStage(state, refName, 'current'),
    priceLabel: Bingo.selectors.getPriceLabel(
      state,
      refName,
      inPlay || inProgress ? 'current' : 'next'
    ),
    inProgressStatus: Bingo.selectors.getInProgressStatus(state, refName, 'current')
  };
};

const BingoInfoPanelAdapter = connect(mapStateToProps)(_BingoInfoPanel);

BingoInfoPanelAdapter.propTypes = {
  refName: PropTypes.string.isRequired
};

export default BingoInfoPanelAdapter;
